/**
 * NotifyInterestModal — form for demo professionals.
 *
 * Fires POST /api/marketplace/notify-interest with the demo pro's id.
 * Response handling mirrors ConsultationRequestModal for consistency:
 *   - 201                                            → success confirmation, closes ~3s
 *   - 422 notify_interest_content_failed_guardrail   → inline rephrase copy
 *   - 429 notify_interest_rate_limited               → daily-limit copy
 *   - 5xx                                            → retryable inline error + Retry button
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { api, formatApiError } from "@/api/client";

const NOTE_MAX = 500;

// Human-readable category label. Kept static so the modal never
// invents new copy — extend when the backend adds new slugs.
const CATEGORY_LABELS = {
  grant_writing: "Grant Writing",
  budget_planning: "Budget Planning",
  cpa_services: "CPA Services",
  nonprofit_formation: "Nonprofit Formation",
  business_formation: "Business Formation",
  credit_repair: "Credit Repair",
  business_plan: "Business Plan",
  sales_marketing: "Sales & Marketing",
};

function primaryCategoryLabel(categories) {
  if (!Array.isArray(categories) || !categories.length) return "matching";
  const first = categories[0];
  return CATEGORY_LABELS[first] || first.replace(/_/g, " ");
}

export const NOTIFY_COPY = {
  title: "Notify me when real professionals join",
  descriptionPrefix:
    "This is a demo profile. Add your name to the notify list and we'll email you when real ",
  descriptionSuffix: " professionals sign up in this marketplace.",
  successHeadlinePrefix: "Thanks — we'll notify you when real ",
  successHeadlineSuffix: " professionals join.",
  guardrailInline:
    "This content contains language we can't process. Please rephrase and try again.",
  rateLimit:
    "You've reached the daily limit for notify-me submissions. Please try again tomorrow.",
  retryHint: "Something went wrong. Please try again.",
};

export default function NotifyInterestModal({ open, onOpenChange, professional }) {
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [errorCode, setErrorCode] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const closeTimerRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setNote("");
      setSubmitting(false);
      setError(null);
      setErrorCode(null);
      setSuccessMsg(null);
    }
    return () => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }
    };
  }, [open]);

  const catLabel = useMemo(
    () => primaryCategoryLabel(professional?.categories),
    [professional]
  );

  const canSubmit = !submitting && note.length <= NOTE_MAX;

  async function submit() {
    if (!professional?.id) return;
    setSubmitting(true);
    setError(null);
    setErrorCode(null);
    try {
      const payload = { professional_id: professional.id };
      if (note.trim()) payload.note = note.trim();
      await api.post("/marketplace/notify-interest", payload);
      setSuccessMsg(
        NOTIFY_COPY.successHeadlinePrefix + catLabel + NOTIFY_COPY.successHeadlineSuffix
      );
      closeTimerRef.current = setTimeout(() => {
        onOpenChange?.(false);
      }, 3000);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      const code = typeof detail === "object" ? detail?.code : null;
      if (status === 422 && code === "notify_interest_content_failed_guardrail") {
        setError(NOTIFY_COPY.guardrailInline);
        setErrorCode(code);
      } else if (status === 429) {
        setError(NOTIFY_COPY.rateLimit);
        setErrorCode("notify_interest_rate_limited");
      } else if (status >= 500) {
        setError(formatApiError(err) || NOTIFY_COPY.retryHint);
        setErrorCode("retryable");
      } else {
        setError(formatApiError(err) || NOTIFY_COPY.retryHint);
        setErrorCode("generic");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="notify-interest-modal" className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{NOTIFY_COPY.title}</DialogTitle>
          <DialogDescription>
            {NOTIFY_COPY.descriptionPrefix}{catLabel}{NOTIFY_COPY.descriptionSuffix}
          </DialogDescription>
        </DialogHeader>

        {successMsg ? (
          <div
            data-testid="notify-interest-success"
            className="rounded-md p-4 text-sm"
            style={{
              background: "rgba(52,211,153,0.12)",
              border: "1px solid rgba(52,211,153,0.35)",
              color: "var(--v2-text, #F5F5F0)",
            }}
          >
            <div className="font-semibold">{successMsg}</div>
          </div>
        ) : (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (canSubmit) submit();
            }}
            className="space-y-4"
          >
            <div>
              <label htmlFor="notify-note" className="text-sm font-medium block mb-1">
                Anything specific you're looking for?{" "}
                <span className="text-xs text-[var(--v2-text-muted,#A1A1AA)] font-normal">
                  (optional)
                </span>
              </label>
              <textarea
                id="notify-note"
                data-testid="notify-interest-note-input"
                value={note}
                maxLength={NOTE_MAX}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Anything specific you're looking for? (optional)"
                rows={4}
                className="w-full rounded-md border px-3 py-2 text-sm bg-transparent resize-y"
                style={{ borderColor: "var(--v2-border, rgba(255,255,255,0.18))" }}
              />
              <div
                data-testid="notify-interest-note-counter"
                className="mt-1 text-[11px] text-right"
                style={{ color: "var(--v2-text-muted, #A1A1AA)" }}
              >
                {note.length} / {NOTE_MAX}
              </div>
            </div>

            {error && (
              <div
                data-testid="notify-interest-error"
                data-error-code={errorCode || ""}
                role="alert"
                className="rounded-md p-3 text-sm flex items-start justify-between gap-3"
                style={{
                  background: "rgba(239,68,68,0.10)",
                  border: "1px solid rgba(239,68,68,0.34)",
                  color: "var(--v2-text, #F5F5F0)",
                }}
              >
                <span>{error}</span>
                {errorCode === "retryable" && (
                  <button
                    type="button"
                    data-testid="notify-interest-retry-btn"
                    onClick={submit}
                    className="text-xs font-semibold underline"
                  >
                    Retry
                  </button>
                )}
              </div>
            )}

            <DialogFooter className="pt-2">
              <button
                type="button"
                onClick={() => onOpenChange?.(false)}
                className="px-4 py-2 rounded-md text-sm font-medium border"
                style={{ borderColor: "var(--v2-border, rgba(255,255,255,0.18))" }}
              >
                Cancel
              </button>
              <button
                type="submit"
                data-testid="notify-interest-submit"
                disabled={!canSubmit}
                className="px-4 py-2 rounded-md text-sm font-semibold disabled:opacity-50 inline-flex items-center gap-2"
                style={{ background: "var(--v2-accent, #5D50C8)", color: "var(--v2-accent-ink, #FFFFFF)" }}
              >
                {submitting && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />}
                {submitting ? "Submitting…" : "Notify me"}
              </button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
